import { useReducer, useEffect, useRef } from "react";
import energyConsumptionReducer from "./reducer";
import { fetchEnergyConsumption } from "./api";
import ACTIONS from "./actions";
import { getEnergyConsumptionDateFormat, transformEnergyConsumptionData } from "../../../../lib/utils/utility";

const initialState = {
  isLoading: false,
  isError: false,
  data: [],
};

const FETCH_DEBOUNCE_MS = 1000;

async function getEnergyConsumptionDetails(dispatch, chargerId, timeRange, siteId, siteTimezone) {
  const startTime = getEnergyConsumptionDateFormat(new Date(timeRange[0]), siteTimezone);
  const endTime = getEnergyConsumptionDateFormat(new Date(timeRange[1]), siteTimezone);
  dispatch({ type: ACTIONS.FETCH_ENERGY_CONSUMPTION_INIT });
  let energyConsumptionData;
  try {
    energyConsumptionData = await fetchEnergyConsumption(siteId, startTime, endTime, chargerId);
  } catch {
    dispatch({ type: ACTIONS.FETCH_ENERGY_CONSUMPTION_FAILURE });
    return;
  }
  if (energyConsumptionData) {
    const data = energyConsumptionData?.e_c;
    const finalResult = transformEnergyConsumptionData(
      data,
      startTime,
      endTime,
      timeRange[0],
      timeRange[1],
      siteTimezone
    );
    dispatch({ type: ACTIONS.FETCH_ENERGY_CONSUMPTION_SUCCESS, payload: finalResult });
  } else {
    dispatch({ type: ACTIONS.FETCH_ENERGY_CONSUMPTION_FAILURE });
  }
}

export function useEnergyConsumptionReducer({ chargerId, timeRange, siteId, siteTimezone }) {
  const [state, dispatch] = useReducer(energyConsumptionReducer, initialState);
  const lastFetchRef = useRef(null);
  const timerRef = useRef(null);

  // Stable stringified key so effect deps don't rely on array/object references
  const startMs = timeRange ? new Date(timeRange[0]).getTime() : null;
  const endMs = timeRange ? new Date(timeRange[1]).getTime() : null;

  useEffect(() => {
    if (!chargerId || !startMs || !endMs || !siteId) return;

    const fetchKey = `${siteId}_${chargerId}_${startMs}_${endMs}`;
    if (lastFetchRef.current === fetchKey) return;

    // Clear any pending debounce from a previous render
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      lastFetchRef.current = fetchKey;
      getEnergyConsumptionDetails(dispatch, chargerId, timeRange, siteId, siteTimezone);
    }, FETCH_DEBOUNCE_MS);

    // Do NOT clear timeout on cleanup — let the debounce complete across re-renders
  }, [chargerId, startMs, endMs, siteId, siteTimezone, timeRange]);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    state,
    dispatch,
  };
}

export default useEnergyConsumptionReducer;
